import { useEffect, useState, useMemo } from "react";
import { useApp } from "../context/AppContext";

export default function CocoonKeywordsMap() {
  const { activeId, apiFetch, dark, t, card } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("keywords"); // "keywords" | "ngrams"
  const [search, setSearch] = useState("");
  const [modalItem, setModalItem] = useState(null);
  const [modalPages, setModalPages] = useState(null); // { loading, pages, error }
  const [sortBy, setSortBy] = useState(null); // null | "site" | "sya" | "freq" | "position"
  const [sortDir, setSortDir] = useState("desc"); // "asc" | "desc"
  const clickHeader = (col) => {
    if (sortBy !== col) { setSortBy(col); setSortDir("desc"); return; }
    if (sortDir === "desc") { setSortDir("asc"); return; }
    setSortBy(null); setSortDir("desc"); // back to default
  };

  useEffect(() => {
    if (!modalItem) { setModalPages(null); return; }
    let alive = true;
    setModalPages({ loading: true });
    const q = encodeURIComponent(modalItem._head);
    apiFetch(`/api/projects/${activeId}/cocoon/keyword-pages?q=${q}`)
      .then(r => r.json())
      .then(d => { if (alive) setModalPages({ loading: false, ...d }); })
      .catch(e => { if (alive) setModalPages({ loading: false, error: e.message }); });
    return () => { alive = false; };
  }, [modalItem, activeId, apiFetch]);

  useEffect(() => {
    if (!activeId) return;
    setLoading(true);
    apiFetch(`/api/projects/${activeId}/cocoon/keywords-map`)
      .then(r => r.json()).then(d => setData(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [activeId, apiFetch]);

  const items = useMemo(() => {
    if (!data) return [];
    let all = view === "keywords" ? data.keywords : data.ngrams;
    if (search.trim()) {
      const q = search.toLowerCase();
      all = all.filter(item => {
        const head = view === "keywords" ? item.keyword : item.ngram;
        return head.toLowerCase().includes(q);
      });
    }
    if (sortBy) {
      const get = (it) => {
        if (sortBy === "site") return it.sitePagesCount ?? 0;
        if (sortBy === "sya") return it.pagesCount ?? 0;
        if (sortBy === "freq") return view === "keywords" ? it.totalFrequency : it.totalCount;
        if (sortBy === "position") {
          // null/unranked → push to bottom regardless of direction
          return it.position == null ? (sortDir === "asc" ? Infinity : -Infinity) : it.position;
        }
        return 0;
      };
      const arr = [...all];
      arr.sort((a, b) => {
        const va = get(a), vb = get(b);
        return sortDir === "asc" ? va - vb : vb - va;
      });
      return arr;
    }
    return all;
  }, [data, view, search, sortBy, sortDir]);

  const exportCsv = () => {
    if (!data) return;
    const rows = view === "keywords"
      ? data.keywords.flatMap(k => (k.pages || []).map(p => [k.keyword, k.totalFrequency, k.sitePagesCount ?? 0, k.pagesCount, k.position ?? "", p.url, p.hfKeyword, p.frequency]))
      : data.ngrams.flatMap(n => (n.pages || []).map(p => [n.ngram, n.totalCount, n.sitePagesCount ?? 0, n.pagesCount, p.url, p.hfKeyword, p.count]));
    const header = view === "keywords"
      ? ["keyword", "total_frequency", "site_pages_count", "sya_pages_count", "position", "page_url", "page_hf_keyword", "page_frequency"]
      : ["ngram", "total_count", "site_pages_count", "sya_pages_count", "page_url", "page_hf_keyword", "page_count"];
    const csv = [header, ...rows].map(r => r.map(v => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `cocoon-${view}-${activeId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div style={{ padding: 20, color: t.textMuted }}>Завантаження…</div>;
  if (!data) return <div style={{ padding: 20, color: t.textMuted }}>Немає даних.</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 4 }}>
          <button onClick={() => { setView("keywords"); setExpanded(new Set()); }} style={tabBtn(view === "keywords", dark, t)}>
            🎯 Ключі ({data.stats.uniqueKeywords})
          </button>
          <button onClick={() => { setView("ngrams"); setExpanded(new Set()); }} style={tabBtn(view === "ngrams", dark, t)}>
            📐 N-грами ({data.stats.uniqueNgrams})
          </button>
        </div>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={view === "keywords" ? "🔍 пошук по ключу…" : "🔍 пошук по n-грамі…"}
          style={{
            flex: 1, minWidth: 240, padding: "8px 12px", borderRadius: 8, fontSize: 13,
            border: `1px solid ${dark ? "#2a2a3a" : "#e5e7eb"}`,
            background: dark ? "rgba(255,255,255,0.04)" : "#fff", color: t.text, outline: "none",
          }}
        />
        <button onClick={exportCsv} style={{
          padding: "8px 16px", borderRadius: 8, border: `1px solid ${t.border}`, cursor: "pointer",
          background: dark ? "rgba(255,255,255,0.04)" : "#fff", color: t.text, fontSize: 12, fontWeight: 600,
        }}>⬇ Експорт CSV</button>
      </div>

      <div style={{ fontSize: 11, color: t.textMuted }}>
        Кластерів: <b>{data.stats.clusters}</b> · Сторінок на сайті: <b>{data.stats.sitePages || 0}</b> · {view === "keywords" ? `Унікальних ключів: ${data.stats.uniqueKeywords}` : `Унікальних n-грам: ${data.stats.uniqueNgrams}`} · Показано: {items.length}
        {view === "keywords" && data.stats.positionSnapshotDate && (
          <> · Позиції за <b>{new Date(data.stats.positionSnapshotDate).toLocaleDateString("uk-UA")}</b> ({data.stats.positionsTracked} ключів tracked)</>
        )}
      </div>

      <div style={{ ...card, padding: 0, overflow: "hidden" }}>
        <div style={{
          display: "grid",
          gridTemplateColumns: view === "keywords" ? "1fr 90px 110px 80px 60px 60px" : "1fr 90px 110px 80px 60px",
          padding: "10px 14px", fontSize: 10, fontWeight: 700, color: t.textMuted, textTransform: "uppercase",
          letterSpacing: 0.5, background: dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
          borderBottom: `1px solid ${t.border}`,
        }}>
          <div>{view === "keywords" ? "Ключ" : "N-грама"}</div>
          {(() => {
            const SortHead = ({ col, label, tip, align = "right" }) => {
              const active = sortBy === col;
              const arrow = active ? (sortDir === "desc" ? " ▼" : " ▲") : "";
              return (
                <div
                  onClick={() => clickHeader(col)}
                  title={tip}
                  style={{
                    textAlign: align, cursor: "pointer", userSelect: "none",
                    color: active ? "#6366f1" : "inherit",
                  }}
                >{label}{arrow}</div>
              );
            };
            return (
              <>
                <SortHead col="freq" label={view === "keywords" ? "Частота" : "К-сть"} tip="Клік — сортування" />
                <SortHead col="site" label="На сайті" tip="Сторінок сайту, де фраза реально зустрічається. Клік — сортування" />
                <SortHead col="sya" label="У СЯ" tip="Сторінок СЯ. Клік — сортування" />
                {view === "keywords" && <SortHead col="position" label="Поз." tip="Позиція з останнього зняття Serper. Клік — сортування" />}
              </>
            );
          })()}
          <div></div>
        </div>

        {items.length === 0 && (
          <div style={{ padding: 30, textAlign: "center", color: t.textMuted, fontSize: 12 }}>
            Нічого не знайдено
          </div>
        )}

        {items.slice(0, 500).map((item, i) => {
          const head = view === "keywords" ? item.keyword : item.ngram;
          const num = view === "keywords" ? item.totalFrequency : item.totalCount;
          return (
            <button
              key={head}
              type="button"
              onClick={() => setModalItem({ ...item, _head: head, _num: num })}
              style={{
                display: "grid",
                gridTemplateColumns: view === "keywords" ? "1fr 90px 110px 80px 60px 60px" : "1fr 90px 110px 80px 60px",
                padding: "10px 14px", fontSize: 12, cursor: "pointer",
                background: "transparent", color: t.text, border: "none",
                borderTop: i > 0 ? `1px solid ${t.border}` : "none",
                textAlign: "left", width: "100%", fontFamily: "inherit",
              }}
              onMouseEnter={e => e.currentTarget.style.background = dark ? "rgba(99,102,241,0.10)" : "rgba(99,102,241,0.06)"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            >
              <div style={{ color: t.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{head}</div>
              <div style={{ textAlign: "right", fontFamily: "monospace", color: t.text, fontWeight: 600 }}>{num.toLocaleString("uk-UA")}</div>
              <div style={{
                textAlign: "right", fontFamily: "monospace", fontWeight: 700,
                color: item.sitePagesCount > 0 ? "#10b981" : t.textMuted,
              }}>{item.sitePagesCount ?? 0}</div>
              <div style={{ textAlign: "right", fontFamily: "monospace", color: "#6366f1", fontWeight: 600 }}>{item.pagesCount}</div>
              {view === "keywords" && (() => {
                const p = item.position;
                const col = p == null ? t.textMuted : p <= 3 ? "#10b981" : p <= 10 ? "#3b82f6" : p <= 30 ? "#f59e0b" : p <= 100 ? "#ef4444" : t.textMuted;
                return (
                  <div style={{ textAlign: "right", fontFamily: "monospace", fontWeight: 700, color: col }}>
                    {p == null ? "—" : p}
                  </div>
                );
              })()}
              <div style={{ textAlign: "right", fontSize: 10, color: t.textMuted }}>→</div>
            </button>
          );
        })}
        {items.length > 500 && (
          <div style={{ padding: "10px 14px", fontSize: 11, color: t.textMuted, textAlign: "center", borderTop: `1px solid ${t.border}` }}>
            Показано перші 500 з {items.length}. Звузьте пошуком або експортуйте CSV для повного списку.
          </div>
        )}
      </div>

      {modalItem && (
        <div
          onClick={() => setModalItem(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              ...card, padding: 0, maxWidth: 720, width: "100%", maxHeight: "85vh", overflow: "auto",
              background: dark ? "#0f0f1c" : "#fff",
            }}
          >
            <div style={{ padding: "16px 20px", borderBottom: `1px solid ${t.border}`, display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 22 }}>{view === "keywords" ? "🎯" : "📐"}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: t.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{modalItem._head}</div>
                <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2 }}>
                  {view === "keywords" ? "Частота:" : "Кількість:"} <b>{modalItem._num.toLocaleString("uk-UA")}</b>
                  {" · "}
                  Сторінок: <b>{modalItem.pagesCount}</b>
                </div>
              </div>
              <button onClick={() => setModalItem(null)} style={{ background: "transparent", border: "none", fontSize: 22, color: t.textMuted, cursor: "pointer", lineHeight: 1 }}>×</button>
            </div>

            <div style={{ padding: 18 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10 }}>
                Сторінки сайту де фраза реально зустрічається (title / H1 / description / текст)
              </div>

              {modalPages?.loading && (
                <div style={{ padding: 30, textAlign: "center", color: t.textMuted, fontSize: 12 }}>Шукаю по сторінках…</div>
              )}
              {modalPages?.error && (
                <div style={{ padding: 12, color: "#ef4444", fontSize: 12 }}>Помилка: {modalPages.error}</div>
              )}
              {modalPages && !modalPages.loading && !modalPages.error && modalPages.pages?.length === 0 && (
                <div style={{ padding: 20, textAlign: "center", color: t.textMuted, fontSize: 12 }}>
                  Фраза присутня у СЯ кластера, але не знайдена у жодному title/H1/description/body спарсених сторінок.
                </div>
              )}
              {modalPages && !modalPages.loading && modalPages.pages?.length > 0 && (
                <>
                  <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 10 }}>Знайдено сторінок: <b>{modalPages.total}</b></div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {modalPages.pages.map((p, j) => {
                      const cleanUrl = p.url.replace(/^https?:\/\/[^/]+/, "") || "/";
                      const m = p.matches || {};
                      const chips = [];
                      if (m.title) chips.push({ l: "Title", c: "#10b981" });
                      if (m.h1) chips.push({ l: "H1", c: "#10b981" });
                      if (m.description) chips.push({ l: "Description", c: "#3b82f6" });
                      if (m.bodyCount > 0) chips.push({ l: `Текст ×${m.bodyCount}`, c: "#a855f7" });
                      return (
                        <div key={j} style={{
                          padding: "10px 12px", borderRadius: 6,
                          border: `1px solid ${t.border}`,
                          background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.01)",
                        }}>
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <a href={p.url} target="_blank" rel="noopener noreferrer" style={{ color: t.text, textDecoration: "none", fontWeight: 600, fontSize: 13, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{cleanUrl}</a>
                              {p.clusterHf && <div style={{ fontSize: 10, color: t.textMuted, fontStyle: "italic", marginTop: 2 }}>🔑 {p.clusterHf}</div>}
                            </div>
                            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                              {chips.map((ch, i) => (
                                <span key={i} style={{ fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 4, background: ch.c + "22", color: ch.c, whiteSpace: "nowrap" }}>{ch.l}</span>
                              ))}
                            </div>
                          </div>
                          {m.title && p.title && <div style={{ fontSize: 11, color: t.textMuted, marginTop: 5 }}>📄 {p.title}</div>}
                          {m.h1 && p.h1 && p.h1 !== p.title && <div style={{ fontSize: 11, color: t.textMuted, marginTop: 3 }}>📌 H1: {p.h1}</div>}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function tabBtn(active, dark, t) {
  return {
    padding: "6px 14px", borderRadius: 6, border: "none", cursor: "pointer",
    background: active ? "#6366f1" : dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
    color: active ? "#fff" : t.text,
    fontWeight: active ? 700 : 500, fontSize: 12, fontFamily: "inherit",
  };
}
